import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"


// Import components
import ProductGallery from "components/Products/ProductGallery"
import Information from "components/Products/Information"
import Comment from "components/Products/Comment"
import ProductsRecommendation from "components/Products/ProductsRecommendation"

// Import API
import { fetchProductDetail } from "api/ProductDetail"
import { sendViewedProduct } from "api/Viewed_product"
import { Product } from "components/Products/ProductsList"
import ListProductByCate from "components/Products/ListProductByCate"
import { Helmet } from "react-helmet-async"

// ==================== 1. DEFINITIONS & INTERFACES ====================


export interface Promotion {
    id: number;
    name: string;
    slug: string;
    start_date: string;
    end_date: string;
    is_active: number;
    created_at: string;
    updated_at: string;
}

export interface PromotionFlashSale {
    id: number;
    name: string;
    start_time: string;
    end_time: string;
}

export interface Voucher {
    id: number;
    name: string;
    code: string;
    describe: string;
    is_active: number;
    type: number;
    value: number;
    position: number;
    exp_date: string;
    max_discount: number;
    min_amount: number;
    type_customer: number;
    max_use_voucher: number;
    customer_usage_limit: number;
    used_voucher: number;
    avatar: string | null;
    start_date: string;
}

export interface ProductData {
    id: number;
    code: string;
    name: string;
    slug: string;
    price: number;
    discount?: number;
    discount_price?: number;
    avatar: string;
    list_thumbnail: string;

    short_content?: string;
    content?: string;

    seo_page?: {
        meta_title?: string;
        meta_description?: string;
        meta_keywords?: string;
        meta_canonical?: string;
    };

    is_active: number;
    is_highlight: number;
    is_follow: number;
    is_new: number;
    is_limit: number;
    is_stock: number;

    star: number;
    position: number;
    point: number;

    created_at: string;
    updated_at: string;

    product_category?: {
        id: number;
        name: string;
        slug: string;
        voucher_product?: Array<{
            target_id: number;
            voucher_id: number;
            voucher: Voucher | null;
        }>;
    };
    product_category_id?: number;

    promotion?: {
        type: string;
        percent: number;
        detail?: any;
    } | null;

    promotion_flash_sale_product?: Array<{
        product_id: number;
        promotion_flash_sale_id: number;
        percent: number;
        promotion_flash_sale?: PromotionFlashSale | null;
    }>;

    promotion_product?: Array<{
        product_id: number;
        promotion_id: number;
        percent: number;
        promotion?: Promotion | null;
    }>;

    voucher_product?: Array<{
        target_id: number;
        voucher_id: number;
        voucher: Voucher | null;
    }>;

    ingredients?: string;
    indications?: string;
    contraindications?: string;
    dosage?: string;
    drug_interactions?: string;
    storage?: string;
    overdose?: string;
}

// ==================== 2. MAIN COMPONENT ====================

export default function Dashboard() {
    const { slug } = useParams<{ slug: string }>();

    // State lưu trữ dữ liệu thô từ API (hoặc đã qua xử lý nhẹ nếu cần)
    const [product, setProduct] = useState<ProductData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                let userId = undefined;
                const userStr = localStorage.getItem('user');

                if (userStr) {
                    try {
                        const userObj = JSON.parse(userStr);
                        userId = userObj.id;
                    } catch (e) {
                        console.error("Lỗi parse user từ localStorage", e);
                    }
                }

                const response = await fetchProductDetail(slug, userId);

                if (response?.data) {
                    // Xử lý linh hoạt cho cả 2 trường hợp API trả về:
                    // 1. Trả về Object chi tiết sản phẩm (response.data)
                    // 2. Trả về Mảng danh sách sản phẩm (response.data.items) - khi dùng filter

                    let rawData: ProductData | null = null;

                    // Kiểm tra nếu data có thuộc tính 'items' và là mảng
                    if ((response.data as any).items && Array.isArray((response.data as any).items)) {
                        const items = (response.data as any).items;
                        if (items.length > 0) {
                            rawData = items[0] as ProductData;
                        }
                    } else {
                        // Trường hợp trả về object trực tiếp
                        rawData = response.data as unknown as ProductData;
                    }

                    if (rawData && rawData.id) {

                        if (typeof (rawData as any).seo_page === "string") {
                            try {
                                (rawData as any).seo_page = JSON.parse((rawData as any).seo_page);
                            } catch (e) {
                                console.error("Parse seo_page failed", e);
                                (rawData as any).seo_page = {};
                            }
                        }

                        setProduct(rawData);
                        sendViewedProduct(rawData.id);
                    } else {
                        setProduct(null);
                    }
                } else {
                    setProduct(null);
                }
            } catch (error) {
                console.error("Failed to fetch product", error);
                setProduct(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();

    }, [slug]);

    // ==================== RENDER UI ====================

    if (loading) {
        return (
            <>
                <div className="flex justify-center items-center h-[50vh]">
                    Đang tải dữ liệu...
                </div>
            </>
        );
    }

    if (!product) {
        return (
            <>
                <div className="flex justify-center items-center h-[50vh] flex-col gap-2">
                    <h2 className="text-xl font-bold">Không tìm thấy sản phẩm</h2>
                    <Link to="/products" className="text-blue-500 hover:underline">Quay lại danh sách</Link>
                </div>
            </>
        );
    }

    const contentHtml = {
        description: product.content || "",
        ingredients: product.ingredients || "",
        indications: product.indications || "",
        contraindications: product.contraindications || "",
        dosage: product.dosage || "",
        drug_interactions: product.drug_interactions || "",
        storage: product.storage || "",
        overdose: product.overdose || "",
    };

    return (
        <>
            <Helmet>
                <title>{product.name}</title>
                <meta name="description" content={ product.seo_page?.meta_description || product.short_content || product.content?.slice(0, 160) || "Thông tin sản phẩm"} />
                <meta name="keywords" content={ product.seo_page?.meta_keywords || product.name} />
                <link rel="canonical" href={ product.seo_page?.meta_canonical || `https://duoctamanh.com/products/${product.slug}`} />
            </Helmet>
            <div className="styles_wrapperContainer__zxrSf">
                <div className="MuiContainer-root MuiContainer-disableGutters MuiContainer-maxWidthLg" style={{ maxWidth: 1180 }}>

                    {/* Breadcrumbs Navigation */}
                    <div style={{ marginBottom: "10px" }}>
                        <nav className="MuiTypography-root MuiBreadcrumbs-root MuiTypography-body1 MuiTypography-colorTextSecondary">
                            <ol className="MuiBreadcrumbs-ol">
                                <li className="MuiBreadcrumbs-li"><Link className="styles_item_breadcrumb__eYGFd" to="/">Trang chủ</Link></li>
                                <li className="MuiBreadcrumbs-separator">
                                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                </li>
                                <li className="MuiBreadcrumbs-li"><Link className="styles_item_breadcrumb__eYGFd" to="/products">Sản phẩm</Link></li>
                                <li className="MuiBreadcrumbs-separator">
                                    <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                </li>

                                {product.product_category && (
                                    <>
                                        <li className="MuiBreadcrumbs-li">
                                            <Link className="styles_item_breadcrumb__eYGFd" to={`/products?category=${product.product_category.id}`}>
                                                {product.product_category.name}
                                            </Link>
                                        </li>
                                        <li className="MuiBreadcrumbs-separator">
                                            <svg className="MuiSvgIcon-root MuiSvgIcon-fontSizeSmall" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" /></svg>
                                        </li>
                                    </>
                                )}

                                <li className="MuiBreadcrumbs-li">
                                    <span className="styles_item_breadcrumb__eYGFd styles_last_breadcrumb__c7IQm font-bold">
                                        {product.name}
                                    </span>
                                </li>
                            </ol>
                        </nav>
                    </div>

                    {/* Product Gallery */}
                    <ProductGallery product={product} info={contentHtml.description} />

                    {/* Information Detail Sections */}
                    <Information contentHtml={contentHtml} />

                    {/* Comments & Recommendations */}
                    <Comment productId={product?.id} />
                    {/* Lưu ý: Kiểm tra component ProductsRecommendation nhận prop tên là gì (thường là categoryId hoặc product) */}
                    {/* <ProductsRecommendation categoryId={product.product_category?.id} /> */}

                    <div id="BLOCK_RECOMMEND" event-block="BLOCK_RECOMMEND" className="[&>section]:p-0 [&>section]:mt-0 rounded-[10px] mt-4">
                        <section className="styles_container__tcm_S MuiBox-root css-yd8sa2 !p-0 !mt-0" id="just-for-you">
                            <div className="styles_wrapperTitle__0iTyg MuiBox-root css-1lekzkb">
                                <div className="MuiBox-root css-j0ozid">
                                    <h2 className="styles_sectionHeader__IJC9w">
                                        Sản phẩm cùng danh mục
                                    </h2>
                                </div>

                                <Link
                                    className="MuiTypography-root MuiLink-root MuiLink-underlineHover MuiTypography-colorPrimary"
                                    to="/products"
                                >
                                    <div className="MuiBox-root css-axw7ok">
                                        <span className="styles_viewmore__xWSnO">
                                            Xem tất cả
                                        </span>

                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M16.006 11.0818C16.5418 11.5767 16.5418 12.4233 16.006 12.9182L11.5982 16.99C10.7976 17.7295 9.5 17.1617 9.5 16.0718V7.9282C9.5 6.83829 10.7976 6.27045 11.5982 7.01001L16.006 11.0818Z"
                                                fill="currentColor"
                                            />
                                            <rect
                                                x="0.5"
                                                y="0.5"
                                                width="23"
                                                height="23"
                                                rx="11.5"
                                                stroke="currentColor"
                                            />
                                        </svg>
                                    </div>
                                </Link>
                            </div>
                            <ListProductByCate
                                categoryId={product.product_category?.id || product.product_category_id}
                                currentProductId={product.id}
                            />
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
}